/**
 * OSR leads — what one person has switched on or off on this device:
 * the new-lead pop-ups, the alert sound, and the sold and lost pop-ups.
 *
 * Per device on purpose: the shop's front-desk computer and someone's own
 * laptop want different answers. Storage is a convenience, never required:
 * with cookies blocked, iOS Safari throws on any localStorage access, so
 * every read and write is guarded and the defaults apply.
 */
import { useCallback, useEffect, useState } from "react";

const KEY = "chariot.osr.prefs.v1";
const EVENT = "chariot:osr-prefs";
export const OSR_PREF_DEFAULTS = { newLeads: true, sound: true, celebrate: true };

export function readOsrPrefs() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...OSR_PREF_DEFAULTS };
    const p = JSON.parse(raw);
    return {
      newLeads: typeof p.newLeads === "boolean" ? p.newLeads : OSR_PREF_DEFAULTS.newLeads,
      sound: typeof p.sound === "boolean" ? p.sound : OSR_PREF_DEFAULTS.sound,
      celebrate: typeof p.celebrate === "boolean" ? p.celebrate : OSR_PREF_DEFAULTS.celebrate,
    };
  } catch {
    return { ...OSR_PREF_DEFAULTS };
  }
}

export function writeOsrPrefs(next) {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — the choice lasts until the page reloads */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
  } catch {
    /* no window (tests) */
  }
}

/** [prefs, setPref(name, value)] — every open component follows a change. */
export default function useOsrPrefs() {
  const [prefs, setPrefs] = useState(readOsrPrefs);
  useEffect(() => {
    const on = (e) => setPrefs(e.detail || readOsrPrefs());
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  const setPref = useCallback((name, value) => {
    const next = { ...readOsrPrefs(), [name]: value };
    setPrefs(next);
    writeOsrPrefs(next);
  }, []);
  return [prefs, setPref];
}
